import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Tag, Truck, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function PromoModal() {
  const [open, setOpen] = useState(false);
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    api.get("/promos/featured").then(({ data }) => setPromo(data && data.code ? data : null)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!promo) return;
    if (sessionStorage.getItem("dg_promo_seen")) return;
    let timer;
    const start = () => {
      timer = setTimeout(() => setOpen(true), 1400);
    };
    // Wait until age gate is cleared before showing the promo
    if (localStorage.getItem("dg_age_ok") === "true") {
      start();
    } else {
      const iv = setInterval(() => {
        if (localStorage.getItem("dg_age_ok") === "true") {
          clearInterval(iv);
          start();
        }
      }, 500);
      return () => {
        clearInterval(iv);
        clearTimeout(timer);
      };
    }
    return () => clearTimeout(timer);
  }, [promo]);

  const close = () => {
    sessionStorage.setItem("dg_promo_seen", "1");
    setOpen(false);
  };

  if (!open || !promo) return null;

  const minClones = promo.min_clones || 0;
  const cloneText = minClones > 1 ? `${minClones} or more clones` : "a clone";

  return (
    <div
      data-testid="promo-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-up"
      onClick={close}
    >
      <div
        className="relative w-full max-w-3xl grid md:grid-cols-2 bg-[#0B0B0B] border border-emerald-500/30 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="promo-close"
          onClick={close}
          className="absolute top-3 right-3 z-10 text-slate-400 hover:text-white transition-colors"
        >
          <X size={22} />
        </button>

        <div className="hidden md:block relative bg-black">
          <img
            src="/dg-clamshell.png"
            alt="Dark Growers clone packaging"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0B0B0B]/60" />
        </div>

        <div className="p-8 sm:p-10">
          <p className="label-caps text-emerald-400 flex items-center gap-2 mb-4">
            <Tag size={14} /> Limited Offer
          </p>
          <h2 className="font-display text-4xl sm:text-5xl text-slate-50 leading-[0.95]">
            Save <span className="text-emerald-400">{promo.discount_percent}%</span> on clones
          </h2>
          <p className="text-slate-400 text-sm mt-5 leading-relaxed">
            Add <span className="text-slate-100 font-medium">{cloneText}</span> to your cart, then
            use code <span className="text-emerald-400 font-semibold">{promo.code}</span> at checkout for{" "}
            <span className="text-emerald-400 font-medium">{promo.discount_percent}% off your entire order</span>.
          </p>
          <p className="flex items-center gap-2 text-xs text-slate-500 mt-4">
            <Truck size={14} className="text-emerald-500" /> Plus free next-day shipping on every order.
          </p>
          <Link
            to="/shop/clones"
            data-testid="promo-shop-clones"
            onClick={close}
            className="group inline-flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-500 transition-colors font-medium px-8 py-3.5 text-sm tracking-wide mt-8"
          >
            Shop Clones
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
}
